import { fetchUserRolesAndPermissions } from '@/api/user';
import type { RetryCheckUpgradeStatusOptions } from './types';

interface RetryCheckUpgradeStatusResult {
  success: boolean;
}

export const retryCheckUpgradeStatus = async (
  options: RetryCheckUpgradeStatusOptions
): Promise<RetryCheckUpgradeStatusResult> => {
  const {
    pollingInterval = 2000,
    maxRetries = 4,
    throwOnError = true,
    expectedRole = 'Essentials',
    useExponentialBackoff = true,
    userId,
  } = options ?? {};

  const defaultResponse: RetryCheckUpgradeStatusResult = {
    success: false,
  };

  let attempt = 0;

  while (attempt < maxRetries) {
    const response = await fetchUserRolesAndPermissions(userId);

    if (response?.role?.includes(expectedRole)) {
      return { success: true };
    }

    if (response?.role && attempt + 1 === maxRetries) {
      // On the last attempt, return the status code anyway
      return defaultResponse;
    }

    attempt += 1;

    if (attempt < maxRetries) {
      // Calculate delay based on whether exponential backoff is enabled
      const delay = useExponentialBackoff
        ? pollingInterval * 2 ** (attempt - 1)
        : pollingInterval;

      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
    }
  }

  if (throwOnError) {
    throw new Error('Failed to check subscription status');
  }

  return defaultResponse;
};
